import React, { useEffect, useRef, useState } from 'react';
import type { GlobalProps } from '@bloomberg/media-ui-core';
import {
  classes,
  CodeInputForm,
  NotificationBanner,
  TransparentButton,
} from '@bloomberg/media-ui-core';
import RegUIClient, { RegUIClientSocialLoginResponse } from 'reg-ui-client';

import { RegGoogleTracking, RegNewRelicLogging } from '../utils/AnalyticsUtils';
import { setThemeFromVariant } from '../utils/helpers';
import { onVerificationCodeInput } from '../utils/regUIHelpers';

import styles from './styles.module.css';

export const CODE_ERROR_MESSAGE =
  'The entered code is not correct. Please try again.';
export const CODE_RESENT_MESSAGE = 'A code has been sent to your email.';

export type Props = GlobalProps & {
  regUIClient?: RegUIClient;
  email: string;
  onEmailChange: () => void;
  codeReSent: boolean;
  onResendEmail: () => void;
  onVerificationCodeInputStarting?: () => void;
  onVerificationCodeInputComplete?: () => void;
  onVerificationCodeSubmit: (
    userDataResp: RegUIClientSocialLoginResponse
  ) => void;
  marketingConsent?: boolean;
  allowEditEmail?: boolean;
  leadSource?: string;
  /** @deprecated use theme */
  variant?: 'default' | 'dark';
  theme?: 'light' | 'dark';
  setIsLoggedIn?: (isLoggedIn: boolean) => void;
  googleTracking?: RegGoogleTracking;
  nr?: RegNewRelicLogging;
  disabled?: boolean;
};

export type CodeInputHandle = {
  clearForm: () => void;
  tagName: string;
  element: HTMLFormElement | null;
};

export const VerificationCodeInput = React.forwardRef<CodeInputHandle, Props>(
  function VerificationCodeInput(props: Props, ref) {
    const {
      email,
      onEmailChange,
      codeReSent,
      onResendEmail,
      onVerificationCodeInputStarting,
      onVerificationCodeInputComplete,
      onVerificationCodeSubmit,
      marketingConsent,
      allowEditEmail,
      leadSource,
      setIsLoggedIn,
      googleTracking,
      nr,
      disabled = false,
    } = props;

    if (!props.regUIClient) {
      // eslint-disable-next-line no-console
      console.warn(
        'Please pass in a regUIClient parameter from the client instead!'
      );
    }
    const theme = setThemeFromVariant(props.theme, props.variant);

    const codeInputWrapperRef = useRef<HTMLDivElement>(null);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showCodeError, setShowCodeError] = useState<boolean>(false);
    const [regUIClient, setRegUIClient] = useState<RegUIClient | undefined>(
      props.regUIClient
    );

    useEffect(() => {
      if (regUIClient) {
        return;
      }

      setRegUIClient(new RegUIClient());
    }, []);

    const onCodeSubmit = async (code: string): Promise<void> => {
      /* c8 ignore next */
      if (!regUIClient) {
        return;
      }

      // we can't tell if this is login or register anymore, so default to register
      googleTracking?.trackRegisterEvent('submit_code');
      nr?.logRegisterEvent('submit_code');

      setIsLoading(true);
      onVerificationCodeInputStarting?.();

      const respInfo = await onVerificationCodeInput(
        regUIClient,
        email,
        code,
        Boolean(marketingConsent),
        leadSource
      );

      onVerificationCodeInputComplete?.();
      if (!respInfo?.success) {
        setShowCodeError(true);
        // eslint-disable-next-line no-console
        console.error(
          respInfo?.error?.errorMessage || 'Error verifying the code'
        );
        googleTracking?.trackErrorEvent('error: invalid_token');
        nr?.logErrorEvent('error: invalid_token');
      } else if (respInfo.data) {
        if (respInfo.data.firstTimeUser) {
          googleTracking?.trackRegisterEvent('account_verified');
          nr?.logRegisterEvent('account_verified');
        } else {
          googleTracking?.trackLoginEvent('account_verified');
          nr?.logLoginEvent('account_verified');
        }
      }

      onVerificationCodeSubmit({
        ...respInfo,
        ...(respInfo?.data && Object.keys(respInfo.data).length > 0
          ? { data: { ...respInfo.data, email }, success: respInfo?.success }
          : { success: false }),
      });
      setIsLoggedIn?.(respInfo?.success || false);

      setIsLoading(false);
    };

    /* c8 ignore next */
    useEffect(() => {
      const codeInput = codeInputWrapperRef.current;

      /* c8 ignore next */
      const clearError = (): void => {
        if (showCodeError) {
          setShowCodeError(false);
        }
      };

      /* c8 ignore next */
      codeInput?.addEventListener('keydown', clearError);

      /* c8 ignore next */
      return (): void => codeInput?.removeEventListener('keydown', clearError);
    }, [showCodeError]);

    return (
      <div className={classes([styles.verificationCodeInputWrapper])}>
        <div className={classes([styles.verificationCodeInput])}>
          <div
            className={classes([
              styles.instructionWrapper,
              {
                [styles.textWhite]: theme === 'dark',
              },
            ])}
          >
            <span className={classes([styles.instructionTitle])}>
              Please enter the code sent to:
            </span>
            <span className={classes([styles.instructionMessage])}>
              <b>{email}</b>
            </span>
          </div>
          <div
            ref={codeInputWrapperRef}
            className={classes([styles.codeInputForm])}
          >
            <CodeInputForm
              className={styles.codeInputForm}
              onFormSubmit={onCodeSubmit}
              length={8}
              loading={isLoading}
              ref={ref}
              showError={showCodeError}
              errorMessage={
                /* c8 ignore next */ showCodeError
                  ? CODE_ERROR_MESSAGE
                  : undefined
              }
              variant={theme === 'dark' ? 'dark' : 'default'}
              disabled={disabled}
            />
          </div>
          {!disabled && (
            <div
              className={classes([
                styles.notificationWrapper,
                {
                  [styles.textWhite]: theme === 'dark',
                },
              ])}
            >
              {codeReSent ? (
                <NotificationBanner
                  variant="success"
                  title={CODE_RESENT_MESSAGE}
                />
              ) : (
                <div
                  className={classes([
                    styles.secondaryCtaWrapper,
                    { [styles.textWhite]: theme === 'dark' },
                  ])}
                >
                  <span>Didn’t get anything?&nbsp;</span>
                  <div>
                    <TransparentButton
                      className={styles.buttonLooksLikeALink}
                      onClick={onResendEmail}
                      type="button"
                      version="phoenix"
                      colorMode={theme === 'dark' ? 'dark' : 'light'}
                    >
                      Resend the code
                    </TransparentButton>
                    {allowEditEmail && (
                      <span>
                        {' or '}
                        <TransparentButton
                          className={styles.buttonLooksLikeALink}
                          disabled={isLoading}
                          onClick={onEmailChange}
                          type="button"
                          version="phoenix"
                          colorMode={theme === 'dark' ? 'dark' : 'light'}
                        >
                          Edit email address
                        </TransparentButton>
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
);
